<template>
  <div class="flex h-screen bg-gray-100">
    <div class="w-64 bg-blue-600 text-white flex flex-col shadow-lg h-screen">
      <div class="p-6 border-b border-blue-500">
        <h1 class="text-xl font-bold">Billing System</h1>
      </div>

      <div class="p-4 border-b border-blue-500">
        <div v-if="userStore.loading" class="flex items-center">
          <div
            class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"
          ></div>
          <span class="text-sm">Загрузка...</span>
        </div>
        <div v-else-if="userStore.user" class="space-y-2">
          <div class="font-medium text-sm">
            {{ userStore.getUserFullName() }}
          </div>
          <div class="text-xs text-blue-200">{{ userStore.user.email }}</div>
          <div
            v-if="userStore.user.role && userStore.user.role.length > 0"
            class="text-xs text-blue-200"
          >
            Роли: {{ userStore.user.role.join(', ') }}
          </div>
        </div>
        <div v-else class="text-sm text-blue-200">Пользователь не загружен</div>
      </div>

      <nav class="flex-1 p-4 space-y-2 overflow-y-auto">
        <div>
          <button
            @click="toggleReferenceMenu"
            class="w-full flex items-center justify-between p-3 text-white cursor-pointer rounded-lg transition-colors duration-200 hover:bg-blue-500"
          >
            <div class="flex items-center">
              <i class="pi pi-book mr-3"></i>
              <span class="font-medium">Справочники</span>
            </div>
            <i
              :class="[
                'pi transition-transform duration-200',
                isReferenceMenuOpen ? 'pi-chevron-down' : 'pi-chevron-right'
              ]"
            ></i>
          </button>

          <div v-show="isReferenceMenuOpen" class="ml-6 mt-2 space-y-1">
            <router-link
              to="/reference/applications"
              class="block p-2 text-blue-200 hover:text-white hover:bg-blue-500 rounded transition-colors duration-200"
              active-class="text-white bg-blue-500"
              >Приложения</router-link
            >
            <router-link
              to="/reference/services"
              class="block p-2 text-blue-200 hover:text-white hover:bg-blue-500 rounded transition-colors duration-200"
              active-class="text-white bg-blue-500"
              >Услуги</router-link
            >
            <router-link
              to="/reference/agencies"
              class="block p-2 text-blue-200 hover:text-white hover:bg-blue-500 rounded transition-colors duration-200"
              active-class="text-white bg-blue-500"
              >Агентства</router-link
            >
            <router-link
              to="/reference/position-decision-makers"
              class="block p-2 text-blue-200 hover:text-white hover:bg-blue-500 rounded transition-colors duration-200"
              active-class="text-white bg-blue-500"
              >Должности ЛПР</router-link
            >
            <router-link
              to="/reference/counterparties"
              class="block p-2 text-blue-200 hover:text-white hover:bg-blue-500 rounded transition-colors duration-200"
              active-class="text-white bg-blue-500"
              >Контрагенты</router-link
            >
            <router-link
              to="/reference/reasons-of-leaving"
              class="block p-2 text-blue-200 hover:text-white hover:bg-blue-500 rounded transition-colors duration-200"
              active-class="text-white bg-blue-500"
              >Причины ухода</router-link
            >
          </div>
        </div>

        <div>
          <button
            @click="toggleAdminMenu"
            class="w-full flex items-center justify-between p-3 text-white cursor-pointer rounded-lg transition-colors duration-200 hover:bg-blue-500"
          >
            <div class="flex items-center">
              <i class="pi pi-book mr-3"></i>
              <span class="font-medium">Admin</span>
            </div>
            <i
              :class="[
                'pi transition-transform duration-200',
                isAdminMenuOpen ? 'pi-chevron-down' : 'pi-chevron-right'
              ]"
            ></i>
          </button>

          <div v-show="isAdminMenuOpen" class="ml-6 mt-2 space-y-1">
            <router-link
              to="/admin/all-projects"
              class="block p-2 text-blue-200 hover:text-white hover:bg-blue-500 rounded transition-colors duration-200"
              active-class="text-white bg-blue-500"
              >Все проекты</router-link
            >
          </div>
        </div>

        <router-link
          to="/payment-accounts"
          class="block p-3 text-white cursor-pointer rounded-lg transition-colors duration-200 hover:bg-blue-500"
          active-class="bg-blue-500"
        >
          <i class="pi pi-credit-card mr-3"></i>
          <span class="font-medium">Платежные аккаунты</span>
        </router-link>

        <router-link
          to="/contracts"
          class="block p-3 text-white cursor-pointer rounded-lg transition-colors duration-200 hover:bg-blue-500"
          active-class="bg-blue-500"
        >
          <i class="pi pi-file mr-3"></i>
          <span class="font-medium">Договоры</span>
        </router-link>

        <router-link
          to="/mrr"
          class="block p-3 text-white cursor-pointer rounded-lg transition-colors duration-200 hover:bg-blue-500"
          active-class="bg-blue-500"
        >
          <i class="pi pi-credit-card mr-3"></i>
          <span class="font-medium">MRR</span>
        </router-link>

        <router-link
          to="/forecast-mrr"
          class="block p-3 text-white cursor-pointer rounded-lg transition-colors duration-200 hover:bg-blue-500"
          active-class="bg-blue-500"
        >
          <i class="pi pi-chart-line mr-3"></i>
          <span class="font-medium">Прогнозный МРР</span>
        </router-link>

        <router-link
          to="/payment-periods"
          class="block p-3 text-white cursor-pointer rounded-lg transition-colors duration-200 hover:bg-blue-500"
          active-class="bg-blue-500"
        >
          <i class="pi pi-calendar mr-3"></i>
          <span class="font-medium">Платежные периоды</span>
        </router-link>

        <router-link
          to="/forecasts"
          class="block p-3 text-white cursor-pointer rounded-lg transition-colors duration-200 hover:bg-blue-500"
          active-class="bg-blue-500"
        >
          <i class="pi pi-chart-line mr-3"></i>
          <span class="font-medium">Прогнозы</span>
        </router-link>

        <router-link
          to="/space-limits"
          class="block p-3 text-white cursor-pointer rounded-lg transition-colors duration-200 hover:bg-blue-500"
          active-class="bg-blue-500"
        >
          <i class="pi pi-table mr-3"></i>
          <span class="font-medium">Лимиты</span>
        </router-link>

        <router-link
          to="/space-limits-management"
          class="block p-3 text-white cursor-pointer rounded-lg transition-colors duration-200 hover:bg-blue-500"
          active-class="bg-blue-500"
        >
          <i class="pi pi-sliders-h mr-3"></i>
          <span class="font-medium">Лимиты по пространствам</span>
        </router-link>
      </nav>

      <div class="mt-auto p-4 border-t border-blue-500">
        <button
          @click="handleLogout"
          class="w-full flex items-center justify-center p-3 text-white cursor-pointer rounded-lg transition-colors duration-200 hover:bg-blue-500 bg-white/10 border border-white/20"
        >
          <i class="pi pi-sign-out mr-3"></i>
          <span class="font-medium">Выйти из системы</span>
        </button>
      </div>
    </div>

    <main class="flex-1 overflow-auto">
      <router-view />
    </main>

    <Toast />
    <ConfirmDialog />
  </div>
</template>

<script>
  import { ref } from 'vue'
  import { useAuth } from './composables/useAuth'
  import { useUserStore } from './stores/user'
  import { useToast } from 'primevue/usetoast'

  export default {
    name: 'App',
    setup() {
      const { logout } = useAuth()
      const userStore = useUserStore()
      const toast = useToast()

      const isReferenceMenuOpen = ref(false)
      const isAdminMenuOpen = ref(false)

      const toggleReferenceMenu = () => {
        isReferenceMenuOpen.value = !isReferenceMenuOpen.value
      }

      const toggleAdminMenu = () => {
        isAdminMenuOpen.value = !isAdminMenuOpen.value
      }

      const handleLogout = async () => {
        try {
          await logout()
        } catch (_error) {
          toast.add({
            severity: 'error',
            summary: 'Ошибка',
            detail: 'Ошибка при выходе из системы',
            life: 3000
          })
        }
      }

      return {
        userStore,
        isReferenceMenuOpen,
        toggleReferenceMenu,
        toggleAdminMenu,
        isAdminMenuOpen,
        handleLogout
      }
    }
  }
</script>

<style scoped>
  .sidebar::-webkit-scrollbar {
    width: 4px;
  }

  .sidebar::-webkit-scrollbar-track {
    background: transparent;
  }

  .sidebar::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 2px;
  }

  .sidebar::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
</style>
